import { createContext, useContext, ReactNode } from 'react';

// Configuration options for notifications
export interface NotificationConfig {
  autoHideDelay?: number; // in milliseconds, 0 means no auto-hide
  position?: 'top-center' | 'top-right' | 'top-left' | 'bottom-center' | 'bottom-right' | 'bottom-left';
  width?: string;
  duration?: number; // animation duration in milliseconds
  offsetFromEdge?: string; // Tailwind distance class (e.g., 'top-24', 'top-[250px]', 'bottom-8')
}

// Factory function type for creating configured notifications
interface NotifyFactory {
  (config?: NotificationConfig): NotifyContextType;
}

// Create a context for the notification functionality
interface NotifyContextType {
  success: (message: string, config?: NotificationConfig) => void;
  error: (title: string, options?: { description?: string; config?: NotificationConfig }) => void;
  info: (title: string, message?: string, config?: NotificationConfig) => void;
  warning: (title: string, message?: string, config?: NotificationConfig) => void;
}

export const NotifyContext = createContext<NotifyFactory | null>(null);

// Provider component that accepts notification handlers as props
export function NotifyProvider({
  children,
  showSuccess,
  showError,
  showInfo,
  showWarning
}: {
  children: ReactNode;
  showSuccess: (title: string, message: string | string[], config?: NotificationConfig) => void;
  showError: (title: string, message: string | string[], config?: NotificationConfig) => void;
  showInfo: (title: string, message: string | string[], config?: NotificationConfig) => void;
  showWarning: (title: string, message: string | string[], config?: NotificationConfig) => void;
}) {
  const notifyFactory: NotifyFactory = (defaultConfig?: NotificationConfig) => {
    return {
      success: (message: string, callConfig?: NotificationConfig) => {
        const mergedConfig = { ...defaultConfig, ...callConfig };
        showSuccess('Success', message, mergedConfig);
      },
      error: (title: string, options?: { description?: string; config?: NotificationConfig }) => {
        const message = options?.description || title;
        const displayTitle = options?.description ? title : 'Error';
        const mergedConfig = { ...defaultConfig, ...options?.config };

        // Handle formatted error messages (with line breaks for bulleted lists)
        if (typeof message === 'string' && message.includes('\n')) {
          const messages = message.split('\n').filter(msg => msg.trim());
          showError(displayTitle, messages, mergedConfig);
        } else {
          showError(displayTitle, message, mergedConfig);
        }
      },
      info: (title: string, message?: string, callConfig?: NotificationConfig) => {
        const mergedConfig = { ...defaultConfig, ...callConfig };
        showInfo(title, message || title, mergedConfig);
      },
      warning: (title: string, message?: string, callConfig?: NotificationConfig) => {
        // Default warning config with longer autoHideDelay
        const warningDefaults = { autoHideDelay: 8000 };
        const mergedConfig = { ...warningDefaults, ...defaultConfig, ...callConfig };
        showWarning(title, message || title, mergedConfig);
      },
    };
  };

  return (
    <NotifyContext.Provider value={notifyFactory}>
      {children}
    </NotifyContext.Provider>
  );
}

// Hook to use notifications in components
export function useNotify(config?: NotificationConfig): NotifyContextType {
  const notifyFactory = useContext(NotifyContext);
  if (!notifyFactory) {
    throw new Error('useNotify must be used within a NotifyProvider');
  }
  return notifyFactory(config);
}